import { useCallback, useEffect, useState } from 'react';

import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { Privacy } from '../components/privacy/types';
import {
	AudioAttachment,
	EmployeeSpotlightAttachment,
	ImageAttachment,
	LinkPreviewAttachment,
	SocialAttachment,
	VideoAttachment,
} from '../lib/entities/attachment';
import { ValidationError } from '../lib/validation';

type AttachmentType = VideoAttachment | ImageAttachment | SocialAttachment | EmployeeSpotlightAttachment | LinkPreviewAttachment | AudioAttachment;

interface ActionInputStateConfig {
	initialAttachment?: AttachmentType | null;
	initialIncludeGiver?: boolean;
	initialPrivacy?: Privacy;
	initialPrivacyButtonEnabled?: boolean;
	onStartDraft?: () => void;
}

export const useActionInputState = ({
	initialAttachment = null,
	initialIncludeGiver = false,
	initialPrivacy = Privacy.Public,
	initialPrivacyButtonEnabled = true,
	onStartDraft,
}: ActionInputStateConfig) => {
	const [attachment, setAttachment] = useState<AttachmentType | null>(initialAttachment);
	const [errors, setErrors] = useState<ValidationError[]>([]);
	const [includeGiver, setIncludeGiver] = useState(initialIncludeGiver);
	const [isAltered, setIsAltered] = useState(false);
	const [privacy, setPrivacy] = useState(initialPrivacy);
	const [privacyButtonEnabled, setPrivacyButtonEnabled] = useState(initialPrivacyButtonEnabled);
	const { hashtagging_mode, zero_point_bonuses } = useCurrentCompany();

	// Sync attachment state when initialAttachment prop changes (e.g., modal reopens with different attachment)
	useEffect(() => {
		setAttachment(initialAttachment);
	}, [initialAttachment]);
	const { flash_points_balance } = useCurrentUser();
	const hashtagMode = hashtagging_mode;
	const zeroPointBonuses = zero_point_bonuses;
	const flashPointsBalance = flash_points_balance;

	const removeAttachment = useCallback(() => {
		setAttachment(null);
	}, []);

	const reset = useCallback(() => {
		setAttachment(null);
		setErrors([]);
		setIsAltered(false);
		setPrivacy(initialPrivacy);
		setPrivacyButtonEnabled(initialPrivacyButtonEnabled);
	}, [initialPrivacy, initialPrivacyButtonEnabled]);

	const startDraft = useCallback(() => {
		setIsAltered(true);

		if (!isAltered && onStartDraft) {
			onStartDraft();
		}
	}, [isAltered, onStartDraft]);

	return {
		attachment,
		errors,
		flashPointsBalance,
		hashtagMode,
		includeGiver,
		isAltered,
		privacy,
		privacyButtonEnabled,
		zeroPointBonuses,
		removeAttachment,
		reset,
		setAttachment,
		setErrors,
		setIncludeGiver,
		setPrivacy,
		setPrivacyButtonEnabled,
		startDraft,
	};
};

export type UseActionInputState = ReturnType<typeof useActionInputState>;
